import { apiGet } from './fetcher';

/** Almost everything in Ullr is read per sport + resort (`/{sport}/areas/{areaId}/…`). */
export interface AreaScope {
  sport: string;
  areaId: string;
}

/**
 * The SWR key convention: `[resource, sport, areaId, ...ids]`.
 *
 * - `resource` is the resource's registered name, camelCase (`'formFolders'`).
 * - `sport` and `areaId` scope it to a resort, so switching resort or sport is a
 *   different key, never another resort's data.
 * - `ids` narrow it further for single-item reads (`['userGroup', sport, areaId, groupId]`).
 *
 * Build keys with `resource.key(...)`, never by hand, so the shape stays one
 * thing a filter (`mutate(key => …)`) can rely on.
 */
export type AreaResourceKey = readonly [resource: string, sport: string, areaId: string, ...ids: string[]];

type AreaFetch<Data> = (scope: AreaScope, ...ids: string[]) => Promise<Data>;

export interface AreaResource<Data> {
  name: string;
  /**
   * The SWR key, or `null` (SWR's "don't fetch") until the scope is known —
   * e.g. before the resort context has loaded, or when the caller is disabled.
   */
  key: (scope: Partial<AreaScope> | null | undefined, ...ids: string[]) => AreaResourceKey | null;
  /** The fetcher for this resource's keys; pass it to `useSWR`. */
  fetcher: (key: AreaResourceKey) => Promise<Data>;
  /** Whether an SWR key belongs to this resource (and, given a scope, that resort). */
  matches: (key: unknown, scope?: AreaScope) => boolean;
}

// name → fetch, so the app-wide default fetcher can serve any registered key.
const registry = new Map<string, AreaFetch<unknown>>();

/**
 * Declare a resort-scoped resource once; hooks read it with
 * `useSWR(resource.key(scope), resource.fetcher)`.
 *
 * Give `path` for a plain GET through the app's axios instance, or `fetch` to
 * reuse an existing `ullrApi` service function (one home for the URL, and the
 * function existing tests already mock). A fetch must reject on failure: no
 * toasts, no swallowing — SWR keeps the last good data and exposes `error`.
 */
export function defineAreaResource<Data>(
  options: { name: string } & (
    | { fetch: AreaFetch<Data> }
    | { path: (scope: AreaScope, ...ids: string[]) => string }
  ),
): AreaResource<Data> {
  const { name } = options;
  const fetch: AreaFetch<Data> = 'fetch' in options
    ? options.fetch
    : (scope, ...ids) => apiGet<Data>(options.path(scope, ...ids));
  // Re-defining a name (hot reload) replaces it.
  registry.set(name, fetch as AreaFetch<unknown>);

  return {
    name,
    key: (scope, ...ids) => (
      scope?.sport && scope?.areaId ? [name, scope.sport, scope.areaId, ...ids] : null
    ),
    fetcher: ([, sport, areaId, ...ids]) => fetch({ sport, areaId }, ...ids),
    matches: (key, scope) => (
      Array.isArray(key)
      && key[0] === name
      && (!scope || (key[1] === scope.sport && key[2] === scope.areaId))
    ),
  };
}

/**
 * The app-wide default fetcher (see `DataProvider`): serves any key built by a
 * registered resource, so `useSWR(resource.key(scope))` works without passing
 * a fetcher. Hooks still pass `resource.fetcher` so they don't depend on the
 * provider being above them.
 */
export function fetchAreaResource(key: AreaResourceKey): Promise<unknown> {
  const [name, sport, areaId, ...ids] = key;
  const fetch = registry.get(name);
  if (!fetch) {
    return Promise.reject(new Error(`No data resource is registered as "${name}"`));
  }
  return fetch({ sport, areaId }, ...ids);
}
